import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  VoiceRouteEditForm,
  VoiceRouteEditInitialValues,
  VoiceRouteComponents,
  VoiceRouteAdditionalParams,
  VoiceRouteFormOptions,
  onVoiceRouteFormSaved,
  IVoiceRouteFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedVoiceRoute, ISharedPatchVoiceRoute, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchVoiceRoute, patchVoiceRoute, getVoiceRouteUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, useVoiceTrunks, useJourneies, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedVoiceRoute> {}

const VoiceRouteFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();
  const voice_trunks = useVoiceTrunks();
  const journeies = useJourneies();

  useEffect(() => {
    fetchVoiceRoute(id).then(voice_route => setValue(voice_route));
  }, [id, setValue, ]);

  const param_props: IVoiceRouteFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
    voice_trunks: voice_trunks.voice_trunks,
    journeies: journeies.journeies,
  };
  const formLayout: IForm | undefined = useMemo(() => value && VoiceRouteEditForm(value, param_props, t), [value, organizations, voice_trunks, journeies, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && VoiceRouteFormOptions(value, param_props, t), [value, organizations, voice_trunks, journeies, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchVoiceRoute, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchVoiceRoute) => value && save(() => patchVoiceRoute(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onVoiceRouteFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !voice_trunks.loaded || !journeies.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.VoiceRoute, value)}
    >
      <AtlasForm<ISharedPatchVoiceRoute>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={VoiceRouteComponents}
        additionalParams={VoiceRouteAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={VoiceRouteEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('VOICE_ROUTE')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const VoiceRouteFormEditView = React.memo(withEdit(VoiceRouteFormEdit, getVoiceRouteUpdateSchema));
